import React, { forwardRef } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  CategoryDraggableProps,
  CategoryDroppableProps,
  DragDropProviderProps,
} from '../hooks/useCategoryDragDrop.types';

/**
 * Basic DragDropContext wrapper with internal context management
 */
const DragDropProvider: React.FC<DragDropProviderProps> = ({ children, onDragEnd }) => {
  return <DragDropContext onDragEnd={onDragEnd}>{children}</DragDropContext>;
};

/**
 * Basic Droppable wrapper with style and className support
 */
const CategoryDroppable = forwardRef<HTMLDivElement, CategoryDroppableProps>(
  ({ droppableId, type = 'DEFAULT', children, className, style }, ref) => {
    return (
      <Droppable droppableId={droppableId} type={type}>
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={(element) => {
              provided.innerRef(element);
              if (ref && typeof ref === 'function') {
                ref(element);
              } else if (ref && typeof ref === 'object') {
                ref.current = element;
              }
            }}
            className={className}
            style={style}
          >
            {children(provided, snapshot)}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  },
);

CategoryDroppable.displayName = 'CategoryDroppable';

/**
 * Basic Draggable wrapper with style and className support
 */
const CategoryDraggable = forwardRef<HTMLDivElement, CategoryDraggableProps>(
  ({ draggableId, index, children, className, style, isDragDisabled = false }, ref) => {
    return (
      <Draggable draggableId={draggableId} index={index} isDragDisabled={isDragDisabled}>
        {(provided, snapshot) => (
          <div
            ref={(element) => {
              provided.innerRef(element);
              if (ref && typeof ref === 'function') {
                ref(element);
              } else if (ref && typeof ref === 'object') {
                ref.current = element;
              }
            }}
            {...provided.draggableProps}
            className={className}
            style={{
              ...provided.draggableProps.style,
              ...style,
            }}
          >
            {children(provided, snapshot, provided.dragHandleProps)}
          </div>
        )}
      </Draggable>
    );
  },
);

CategoryDraggable.displayName = 'CategoryDraggable';

/**
 * Exported basic components object
 */
export const BasicDragDropComponents = {
  DragDropProvider,
  CategoryDroppable,
  CategoryDraggable,
};
